import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/MarketplacePostHistory.graphql';
import { Query, QuerymarketplacePostHistoryArgs } from '../../__generated__/globalTypes';

export const useMarketplacePostHistoryQuery = (options?: QueryHookOptions<Query, QuerymarketplacePostHistoryArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerymarketplacePostHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useMarketplacePostHistoryLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerymarketplacePostHistoryArgs>
): QueryResult<Query, QuerymarketplacePostHistoryArgs> & {
  getMarketplacePostHistory: LazyQueryExecFunction<Query, QuerymarketplacePostHistoryArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getMarketplacePostHistory, result] = useLazyQuery<Query, QuerymarketplacePostHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getMarketplacePostHistory, ...result };
};

export const useMarketplacePostHistoryPromiseQuery = () => {
  const client = useApolloClient();

  const getMarketplacePostHistory = useCallback(
    (options?: Omit<QueryOptions<QuerymarketplacePostHistoryArgs, Query>, 'query'>) =>
      client.query<Query, QuerymarketplacePostHistoryArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getMarketplacePostHistory };
};
// Suspense Query Hooks
export const useMarketplacePostHistorySuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerymarketplacePostHistoryArgs>
) => {
  const result = useSuspenseQuery<Query, QuerymarketplacePostHistoryArgs>(QUERY, {
    ...options
  });

  return result;
};
